import React, { useState, useEffect, useCallback, useRef } from "react";
import "./styles.css";
import { BarCharts } from "./chart";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Calculator as CalculatorIcon,
  BarChart3,
  Building2,
  AlertTriangle,
  TrendingDown,
  ArrowDownCircle,
} from "lucide-react";
import QRCode from "qrcode";
import { PropertyDetailsSection } from "./PropertyDetailsSection";
import { RenovationOptionsSection } from "./RenovationOptionsSection";
import { FinancingOptionsSection } from "./FinancingOptionsSection";
import { OwnerOptionsSection } from "./OwnerOptionsSection";
import { MarketAnalysisSection } from "./MarketAnalysisSection";
import { ResultsPropertyCards } from "./ResultsPropertyCards";
import { ProfitSummaryCard } from "./ProfitSummaryCard";
import { ProfitDistributionCard } from "./ProfitDistributionCard";
import { ComparisonCard } from "./ComparisonCard";
import { ComparePropertyEnhanced } from "../../pages/property/compare-property-enhanced";
import { DealResultEffect } from "./DealResultEffect";
import API_CONSTRUCTOR from "../../models/api-model";

function HouseFlippingCalculator(props) {
  const {
    buildingSquareFeet = 1500,
    estimatedRentAmount = 1800,
    estimatedValue = 700000,
    loanBalance = 200000,
    lotSquareFeet = 5000,
    marketValuePerSqft = 400,
    setMarketValuePerSqft = () => {},
    setMarketValuePerSqftAuto = setMarketValuePerSqft,
    pool = false,
    property = {},
    onCalculate, // Optional callback to send results to parent
    readOnly = false, // Read-only mode for viewing calculations
    initialInputs = null, // Pre-populated inputs for read-only mode
    isHomeowner = false, // Hide complex financing options for homeowners
    showCompare = false,
    propertyId = null,
  } = props;

  const [activeTab, setActiveTab] = useState("input");
  const api = API_CONSTRUCTOR();
  const calculatorRef = useRef(null);
  const onCalculateRef = useRef(onCalculate);
  const compareOpenRef = useRef(null);
  const lastSyncedPropertyKeyRef = useRef(null);

  // Keep the ref updated with the latest callback
  useEffect(() => {
    onCalculateRef.current = onCalculate;
  }, [onCalculate]);

  // Constants for comparison calculations
  const traditionalAppreciationRate = 0.04; // 4% annual

  // Input States
  const [presentValue, setPresentValue] = useState(estimatedValue);
  const [isPresentValueManuallyEdited, setIsPresentValueManuallyEdited] =
    useState(false);
  const [homeArea, setHomeArea] = useState(buildingSquareFeet);
  const [isHomeAreaManuallyEdited, setIsHomeAreaManuallyEdited] =
    useState(false);
  const [loanAmount, setLoanAmount] = useState(loanBalance);
  const [currentRental, setCurrentRental] = useState(estimatedRentAmount);
  const [lotSize, setLotSize] = useState(lotSquareFeet);
  const [hasPool, setHasPool] = useState(pool ? "yes" : "no");
  const [renovationTier, setRenovationTier] = useState(200);
  const [extraAreaTier, setExtraAreaTier] = useState(400);
  const [addAreaCheckbox, setAddAreaCheckbox] = useState(false);
  const [extraArea, setExtraArea] = useState(0);
  const [investorType, setInvestorType] = useState(15);
  const [rentalPeriod, setRentalPeriod] = useState(12);
  const [guaranteedReturnPct, setGuaranteedReturnPct] = useState(20);
  const [upfrontPaymentPct, setUpfrontPaymentPct] = useState(30);
  const [paymentParts, setPaymentParts] = useState(4);
  const [profitValues, setProfitValues] = useState([15, 30]);
  const [manualComparables, setManualComparables] = useState([]);

  const [profitA, profitB, profitC] = [
    profitValues[0],
    profitValues[1] - profitValues[0],
    100 - profitValues[1],
  ];

  // Initialize from saved inputs (read-only mode OR edit mode with saved data)
  useEffect(() => {
    if (initialInputs) {
      setPresentValue(initialInputs.presentValue || estimatedValue);
      setHomeArea(initialInputs.homeArea || buildingSquareFeet);
      setLoanAmount(initialInputs.loanAmount || loanBalance);
      setCurrentRental(initialInputs.currentRental || estimatedRentAmount);
      setLotSize(initialInputs.lotSize || lotSquareFeet);
      setHasPool(initialInputs.hasPool || (pool ? "yes" : "no"));
      setRenovationTier(initialInputs.renovationTier || 200);
      setExtraAreaTier(initialInputs.extraAreaTier || 400);
      setAddAreaCheckbox(initialInputs.addAreaCheckbox || false);
      setExtraArea(initialInputs.extraArea || 0);
      setInvestorType(initialInputs.investorType || 15);
      setRentalPeriod(initialInputs.rentalPeriod || 12);
      setGuaranteedReturnPct(initialInputs.guaranteedReturnPct || 20);
      setUpfrontPaymentPct(initialInputs.upfrontPaymentPct || 30);
      setPaymentParts(initialInputs.paymentParts || 4);
      if (initialInputs.profitValues) {
        setProfitValues(initialInputs.profitValues);
      }
    }
  }, [initialInputs]);

  // Update calculator inputs only when a new property is loaded.
  // Skip when initialInputs is set so URL-driven numbers are not overwritten.
  useEffect(() => {
    if (readOnly || initialInputs) return;
    const propertyKey =
      propertyId ||
      property?.id ||
      property?.attomId ||
      property?.apn ||
      property?.fips ||
      property?.address ||
      null;
    if (!propertyKey || lastSyncedPropertyKeyRef.current === propertyKey) return;
    lastSyncedPropertyKeyRef.current = propertyKey;
    setManualComparables([]);
    setIsPresentValueManuallyEdited(false);
    setIsHomeAreaManuallyEdited(false);
    setPresentValue(estimatedValue);
    setHomeArea(buildingSquareFeet);
    setLoanAmount(loanBalance);
    setCurrentRental(estimatedRentAmount);
    setLotSize(lotSquareFeet);
    setHasPool(pool ? "yes" : "no");
  }, [
    readOnly,
    initialInputs,
    propertyId,
    property,
  ]);

  useEffect(() => {
    if (readOnly || initialInputs || isPresentValueManuallyEdited) return;
    setPresentValue(estimatedValue);
  }, [readOnly, initialInputs, isPresentValueManuallyEdited, estimatedValue]);

  useEffect(() => {
    if (readOnly || initialInputs || isHomeAreaManuallyEdited) return;
    setHomeArea(buildingSquareFeet);
  }, [readOnly, initialInputs, isHomeAreaManuallyEdited, buildingSquareFeet]);

  const handlePresentValueChange = useCallback((value) => {
    setIsPresentValueManuallyEdited(true);
    setPresentValue(value);
  }, []);

  const handleHomeAreaChange = useCallback((value) => {
    setIsHomeAreaManuallyEdited(true);
    setHomeArea(value);
  }, []);

  // Results States
  const [results, setResults] = useState({
    presentValue: 0,
    currentPriceSqft: 0,
    totalArea: 0,
    loan: 0,
    renovationTierText: "",
    renovationCost: 0,
    extraAreaAdded: 0,
    extraAreaCost: 0,
    upfrontRental: 0,
    constructionLoan: 0,
    loanInterest: 0,
    agentCommission: 0,
    closingCosts: 0,
    guaranteedReturn: 0,
    guaranteedReturnPct: 20,
    marketValue: 0,
    estimatedSalePrice: 0,
    totalInvestment: 0,
    totalFlippingCost: 0,
    totalProfit: 0,
    roi: 0,
    profitMargin: 0,
    ownerProfit: 0,
    investorProfit: 0,
    agentProfit: 0,
    traditionalValue: 0,
    traditionalProfit: 0,
    koflipOwnerReturn: 0,
    progressionData: [],
    upfrontPaymentPct: 30,
    paymentParts: 4,
    upfrontAmount: 0,
    remainingAmount: 0,
    amountPerPart: 0,
    investorPaymentData: [],
    rentalPeriod: 12,
  });
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportData, setReportData] = useState(null);
  const [qrPreviewUrl, setQrPreviewUrl] = useState("");

  const calculateResults = useCallback(() => {
    // Parse input values
    const pv = parseFloat(presentValue) || 0;
    const ha = parseFloat(homeArea) || 0;
    const la = parseFloat(loanAmount) || 0;
    const cr = parseFloat(currentRental) || 0;
    const rt = parseInt(renovationTier);
    const ea = addAreaCheckbox ? parseFloat(extraArea) || 0 : 0;
    const it = parseInt(investorType);
    const rp = parseInt(rentalPeriod);
    const mvpsf = parseFloat(marketValuePerSqft) || 0;
    const grp = parseFloat(guaranteedReturnPct) || 0;

    // ===================================
    // ORIGINAL FRONTEND CALCULATION LOGIC
    // ===================================

    // Basic calculations
    const renovationCostPerSqft = rt;
    const currentRenovationCost = ha * renovationCostPerSqft;
    const currentExtraAreaCost = ea * extraAreaTier;
    const totalConstructionCost = currentRenovationCost + currentExtraAreaCost;

    const currentUpfrontRental = cr * rp; // Monthly rent × rental period (YEARS)
    const annualInterestRate = it / 100;
    const currentConstructionLoan = totalConstructionCost;

    // Calculate staged payment interest
    const upfrontAmount = currentConstructionLoan * (upfrontPaymentPct / 100);
    const remainingAmount = currentConstructionLoan - upfrontAmount;
    const amountPerPart = remainingAmount / paymentParts;
    const monthsBetweenPayments = rp / paymentParts;

    let totalInterest = 0;
    totalInterest += upfrontAmount * annualInterestRate * (rp / 12);

    for (let i = 1; i <= paymentParts; i++) {
      const remainingMonths = rp - i * monthsBetweenPayments;
      const interestOnPart =
        amountPerPart * annualInterestRate * (remainingMonths / 12);
      totalInterest += Math.max(0, interestOnPart);
    }

    const currentLoanInterest = totalInterest;

    const totalAreaResult = ha + ea;
    const currentEstimatedSalePrice = totalAreaResult * mvpsf;
    const currentAgentCommission = currentEstimatedSalePrice * 0.06;
    const currentClosingCosts = currentEstimatedSalePrice * 0.02;

    const guaranteedBuyerReturn = pv * (grp / 100);

    const currentTotalInvestmentBase =
      pv +
      totalConstructionCost +
      currentLoanInterest +
      currentAgentCommission +
      currentClosingCosts +
      currentUpfrontRental;

    const currentTotalInvestment =
      currentTotalInvestmentBase + guaranteedBuyerReturn;
    /** All costs except purchase (present value): construction + interest + rent + guaranteed + commission + closing */
    const totalFlippingCost = currentTotalInvestment - pv;
    const currentTotalProfit =
      currentEstimatedSalePrice - currentTotalInvestment;
    const currentRoi =
      currentTotalInvestment !== 0
        ? (currentTotalProfit / currentTotalInvestment) * 100
        : 0;
    const currentProfitMargin =
      currentEstimatedSalePrice !== 0
        ? (currentTotalProfit / currentEstimatedSalePrice) * 100
        : 0;

    const currentOwnerProfit = (currentTotalProfit * profitA) / 100;
    const currentInvestorProfit = (currentTotalProfit * profitB) / 100;
    const currentAgentProfit = (currentTotalProfit * profitC) / 100;

    // KoFlip vs Traditional comparison calculations
    const constructionPeriodYears = rp / 12;
    const traditionalValue =
      pv * Math.pow(1 + traditionalAppreciationRate, constructionPeriodYears);
    const traditionalProfit = traditionalValue - pv;
    const koflipOwnerReturn =
      guaranteedBuyerReturn + currentUpfrontRental + currentOwnerProfit;

    // Generate monthly progression data for charts
    const generateProgressionData = () => {
      const months = Array.from({ length: rp + 1 }, (_, i) => i);
      const monthlyAppreciationRate = traditionalAppreciationRate / 12;

      return months.map((month) => {
        const traditionalValueAtMonth =
          pv * Math.pow(1 + monthlyAppreciationRate, month);
        const traditionalProfitAtMonth = traditionalValueAtMonth - pv;

        let koflipReturnAtMonth = 0;
        if (month === 0) {
          koflipReturnAtMonth = currentUpfrontRental;
        } else if (month === rp) {
          koflipReturnAtMonth = koflipOwnerReturn;
        } else {
          koflipReturnAtMonth = currentUpfrontRental;
        }

        return {
          month,
          traditional: traditionalProfitAtMonth,
          koflip: koflipReturnAtMonth,
        };
      });
    };

    const generateInvestorPaymentData = () => {
      const months = Array.from({ length: rp + 1 }, (_, i) => i);
      const paymentMonths = [];

      const month0Payment = currentUpfrontRental + upfrontAmount;
      paymentMonths.push({
        month: 0,
        payment: month0Payment,
        cumulative: month0Payment,
        breakdown: {
          rental: currentUpfrontRental,
          construction: upfrontAmount,
        },
      });

      let cumulativeAmount = month0Payment;

      for (let i = 1; i <= paymentParts; i++) {
        const paymentMonth = Math.round((i * rp) / paymentParts);
        cumulativeAmount += amountPerPart;

        paymentMonths.push({
          month: paymentMonth,
          payment: amountPerPart,
          cumulative: cumulativeAmount,
          breakdown: {
            rental: 0,
            construction: amountPerPart,
          },
        });
      }

      let currentCumulative = 0;

      return months.map((month) => {
        const payment = paymentMonths.find((p) => p.month === month);
        if (payment) {
          currentCumulative = payment.cumulative;
        }

        return {
          month,
          cumulative: currentCumulative,
          hasPayment: !!payment,
          paymentAmount: payment ? payment.payment : 0,
          breakdown: payment
            ? payment.breakdown
            : { rental: 0, construction: 0 },
        };
      });
    };

    const progressionData = generateProgressionData();
    const investorPaymentData = generateInvestorPaymentData();

    const calculatedResults = {
      presentValue: parseFloat(presentValue) || 0,
      currentPriceSqft: ha !== 0 ? pv / ha : 0,
      totalArea: totalAreaResult,
      loan: parseFloat(loanAmount) || 0,
      renovationCost: currentRenovationCost,
      totalLoan: currentConstructionLoan,
      extraAreaAdded: ea,
      extraAreaCost: currentExtraAreaCost,
      upfrontRental: currentUpfrontRental,
      constructionLoan: totalConstructionCost,
      loanInterest: currentLoanInterest,
      agentCommission: currentAgentCommission,
      closingCosts: currentClosingCosts,
      guaranteedReturn: guaranteedBuyerReturn,
      guaranteedReturnPct: grp,
      marketValue: mvpsf,
      estimatedSalePrice: currentEstimatedSalePrice,
      rentalPeriod: rp,
      totalInvestment: currentTotalInvestment,
      totalFlippingCost,
      totalProfit: currentTotalProfit,
      roi: currentRoi,
      profitMargin: currentProfitMargin,
      ownerProfit: currentOwnerProfit,
      investorProfit: currentInvestorProfit,
      agentProfit: currentAgentProfit,
      traditionalValue: traditionalValue,
      traditionalProfit: traditionalProfit,
      koflipOwnerReturn: koflipOwnerReturn,
      progressionData: progressionData,
      upfrontPaymentPct: upfrontPaymentPct,
      paymentParts: paymentParts,
      upfrontAmount: upfrontAmount,
      remainingAmount: remainingAmount,
      amountPerPart: amountPerPart,
      investorPaymentData: investorPaymentData,
    };

    setResults(calculatedResults);

    // Call onCalculate callback if provided (for parent components like CalculationEditor)
    if (
      onCalculateRef.current &&
      typeof onCalculateRef.current === "function"
    ) {
      const calculationData = {
        inputs: {
          presentValue: parseFloat(presentValue) || 0,
          homeArea: parseFloat(homeArea) || 0,
          loanAmount: parseFloat(loanAmount) || 0,
          currentRental: parseFloat(currentRental) || 0,
          renovationTier: parseInt(renovationTier) || 200,
          extraArea: parseFloat(extraArea) || 0,
          addAreaCheckbox: addAreaCheckbox,
          extraAreaTier: parseInt(extraAreaTier) || 400,
          investorType: parseInt(investorType) || 15,
          rentalPeriod: parseInt(rentalPeriod) || 12,
          marketValuePerSqft: parseFloat(marketValuePerSqft) || 0,
          guaranteedReturnPct: parseFloat(guaranteedReturnPct) || 20,
          upfrontPaymentPct: parseFloat(upfrontPaymentPct) || 30,
          paymentParts: parseInt(paymentParts) || 4,
          profitDistribution: { profitA, profitB, profitC },
          hasPool: hasPool,
          lotSize: parseFloat(lotSize) || 0,
        },
        results: calculatedResults,
      };
      onCalculateRef.current(calculationData);
    }
  }, [
    extraAreaTier,
    presentValue,
    homeArea,
    loanAmount,
    currentRental,
    renovationTier,
    addAreaCheckbox,
    extraArea,
    investorType,
    rentalPeriod,
    marketValuePerSqft,
    profitA,
    profitB,
    profitC,
    guaranteedReturnPct,
    traditionalAppreciationRate,
    upfrontPaymentPct,
    paymentParts,
    hasPool,
    lotSize,
  ]);

  useEffect(() => {
    calculateResults();
  }, [calculateResults]);

  useEffect(() => {
    if (!addAreaCheckbox) {
      setExtraArea(0);
    }
  }, [addAreaCheckbox]);

  const handleCalculateClick = () => {
    calculateResults();
    setActiveTabFn("results");
  };

  const getPropertyAddress = () => {
    return (
      property?.address ||
      property?.propertyAddress ||
      property?.fullAddress ||
      property?.formattedAddress ||
      "Property Address"
    );
  };

  const buildQrImageWithBranding = useCallback(async (targetUrl) => {
    const qrDataUrl = await QRCode.toDataURL(targetUrl, {
      color: {
        dark: "#c96442",
        light: "#ffffff",
      },
      margin: 2,
      width: 700,
    });

    const [qrImage, logoImage] = await Promise.all([
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = qrDataUrl;
      }),
      new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = "/logo.png";
      }),
    ]);

    const canvas = document.createElement("canvas");
    canvas.width = 900;
    canvas.height = 1140;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Unable to generate QR preview canvas");
    }

    const drawRoundedRect = (x, y, w, h, r) => {
      const radius = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + w - radius, y);
      ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
      ctx.lineTo(x + w, y + h - radius);
      ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
      ctx.lineTo(x + radius, y + h);
      ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
    };

    const bgGradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    bgGradient.addColorStop(0, "#fffaf3");
    bgGradient.addColorStop(0.55, "#fff1e6");
    bgGradient.addColorStop(1, "#ffe7d7");
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const cardX = 60;
    const cardY = 48;
    const cardW = canvas.width - 120;
    const cardH = canvas.height - 96;
    drawRoundedRect(cardX, cardY, cardW, cardH, 34);
    ctx.shadowColor = "rgba(201, 100, 66, 0.22)";
    ctx.shadowBlur = 34;
    ctx.shadowOffsetY = 14;
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.shadowColor = "transparent";

    const borderGradient = ctx.createLinearGradient(cardX, cardY, cardX + cardW, cardY + cardH);
    borderGradient.addColorStop(0, "rgba(201, 100, 66, 0.45)");
    borderGradient.addColorStop(1, "rgba(252, 146, 59, 0.25)");
    ctx.strokeStyle = borderGradient;
    ctx.lineWidth = 2;
    ctx.stroke();

    const qrWrapX = cardX + 56;
    const qrWrapY = cardY + 72;
    const qrWrapW = cardW - 112;
    const qrWrapH = qrWrapW;
    drawRoundedRect(qrWrapX, qrWrapY, qrWrapW, qrWrapH, 24);
    ctx.fillStyle = "#fff8f2";
    ctx.fill();
    ctx.strokeStyle = "#f5d2bf";
    ctx.lineWidth = 2;
    ctx.stroke();

    const qrSize = qrWrapW - 56;
    const qrX = qrWrapX + (qrWrapW - qrSize) / 2;
    const qrY = qrWrapY + (qrWrapH - qrSize) / 2;
    ctx.drawImage(qrImage, qrX, qrY, qrSize, qrSize);

    ctx.fillStyle = "#c96442";
    ctx.font = "700 28px Poppins, Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Scan to Open Full Deal Report", canvas.width / 2, qrWrapY + qrWrapH + 58);

    const logoWidth = 196;
    const logoHeight = 98;
    const logoX = (canvas.width - logoWidth) / 2;
    const logoY = qrWrapY + qrWrapH + 88;
    ctx.drawImage(logoImage, logoX, logoY, logoWidth, logoHeight);

    ctx.fillStyle = "#b4532a";
    ctx.font = "600 34px Poppins, Arial, sans-serif";
    ctx.fillText("Powered by KoFlip", canvas.width / 2, logoY + 146);

    return canvas.toDataURL("image/png");
  }, []);

  const handleGenerateReport = async () => {
    try {
      setReportError("");
      setIsGeneratingReport(true);
      const payload = {
        propertyAddress: getPropertyAddress(),
        ...(propertyId ? { propertyId: String(propertyId) } : {}),
        ...(manualComparables.length
          ? { comparables: manualComparables.slice(0, 3) }
          : {}),
        inputs: {
          presentValue: parseFloat(presentValue) || 0,
          homeArea: parseFloat(homeArea) || 0,
          loanAmount: parseFloat(loanAmount) || 0,
          currentRental: parseFloat(currentRental) || 0,
          renovationTier: parseInt(renovationTier) || 0,
          extraArea: addAreaCheckbox ? parseFloat(extraArea) || 0 : 0,
          addAreaCheckbox: Boolean(addAreaCheckbox),
          extraAreaTier: parseInt(extraAreaTier) || 400,
          rentalPeriod: parseInt(rentalPeriod) || 0,
          marketValuePerSqft: parseFloat(marketValuePerSqft) || 0,
          upfrontPaymentPct: parseFloat(upfrontPaymentPct) || 0,
          paymentParts: parseInt(paymentParts) || 4,
          investorType: parseInt(investorType) || 0,
          guaranteedReturnPct: parseFloat(guaranteedReturnPct) || 0,
          profitValues: [...profitValues],
        },
        results: {
          estimatedSalePrice: results.estimatedSalePrice || 0,
          totalInvestment: results.totalInvestment || 0,
          totalProfit: results.totalProfit || 0,
          roi: results.roi || 0,
          profitMargin: results.profitMargin || 0,
          ownerProfit: results.ownerProfit || 0,
          investorProfit: results.investorProfit || 0,
          agentProfit: results.agentProfit || 0,
          guaranteedReturn: results.guaranteedReturn || 0,
          upfrontRental: results.upfrontRental || 0,
          upfrontAmount: results.upfrontAmount || 0,
          remainingAmount: results.remainingAmount || 0,
          amountPerPart: results.amountPerPart || 0,
          constructionLoan: results.constructionLoan || results.totalLoan || 0,
          loanInterest: results.loanInterest || 0,
        },
      };

      const response = await api.post("/reports", payload);
      const reportUrl = response?.data?.reportUrl;
      const absoluteUrl = `${window.location.origin}${reportUrl}`;
      const brandedQr = await buildQrImageWithBranding(absoluteUrl);
      setQrPreviewUrl(brandedQr);
      setReportData({
        ...response.data,
        absoluteUrl,
      });
    } catch (error) {
      setReportError(
        error?.response?.data?.error || "Failed to generate report. Please try again."
      );
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrPreviewUrl || !reportData?.propertyAddress) return;
    const a = document.createElement("a");
    const safeName = reportData.propertyAddress
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
    a.href = qrPreviewUrl;
    a.download = `koflip-report-qr-${safeName || "property"}.png`;
    a.click();
  };

  const setActiveTabFn = (tab) => {
    setActiveTab(tab);
    if (calculatorRef.current) {
      calculatorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div
      ref={calculatorRef}
      className="min-h-screen py-4 sm:py-6 md:py-8 px-2 sm:px-4 md:px-6 lg:px-8 bg-background"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-4 sm:mb-6 md:mb-10">
          <div className="inline-flex items-center justify-center gap-2 mb-3">
            <CalculatorIcon className="h-6 w-6 sm:h-8 sm:w-8 text-primary flex-shrink-0" />
            <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
              House Flipping Calculator
            </h1>
          </div>
          <p className="text-xs sm:text-sm md:text-base lg:text-lg text-muted-foreground px-2 sm:px-4">
            Analyze your property's potential and maximize your profits
          </p>
        </div>

        <Card className="calculator-container shadow-lg bg-white border border-gray-200">
          <CardContent className="p-2 sm:p-4 md:p-6 lg:p-8">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTabFn}
              className="w-full"
            >
              <TabsList className="grid w-full grid-cols-2 mb-4 sm:mb-6 md:mb-8 h-auto">
                <TabsTrigger
                  value="input"
                  className="text-xs sm:text-sm md:text-base font-medium py-2 sm:py-3 flex items-center justify-center gap-1 sm:gap-2"
                >
                  <Building2 className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="hidden sm:inline whitespace-nowrap">
                    Property Details
                  </span>
                  <span className="sm:hidden whitespace-nowrap">Details</span>
                </TabsTrigger>
                <TabsTrigger
                  value="results"
                  className="text-xs sm:text-sm md:text-base font-medium py-2 sm:py-3 flex items-center justify-center gap-1 sm:gap-2"
                >
                  <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">Results</span>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="input" className="mt-0">
                <div id="input-section" className="space-y-4 sm:space-y-6">
                  <PropertyDetailsSection
                    presentValue={presentValue}
                    setPresentValue={handlePresentValueChange}
                    homeArea={homeArea}
                    setHomeArea={handleHomeAreaChange}
                    loanAmount={loanAmount}
                    setLoanAmount={setLoanAmount}
                    currentRental={currentRental}
                    setCurrentRental={setCurrentRental}
                    lotSize={lotSize}
                    setLotSize={setLotSize}
                    hasPool={hasPool}
                    setHasPool={setHasPool}
                    readOnly={readOnly}
                  />

                  <RenovationOptionsSection
                    homeArea={homeArea}
                    renovationTier={renovationTier}
                    setRenovationTier={setRenovationTier}
                    addAreaCheckbox={addAreaCheckbox}
                    setAddAreaCheckbox={setAddAreaCheckbox}
                    extraArea={extraArea}
                    setExtraArea={setExtraArea}
                    extraAreaTier={extraAreaTier}
                    setExtraAreaTier={setExtraAreaTier}
                    rentalPeriod={rentalPeriod}
                    setRentalPeriod={setRentalPeriod}
                    results={results}
                    readOnly={readOnly}
                  />

                  {/* Only show financing options for investors/non-homeowners */}
                  {!isHomeowner && (
                    <FinancingOptionsSection
                      investorType={investorType}
                      setInvestorType={setInvestorType}
                      rentalPeriod={rentalPeriod}
                      upfrontPaymentPct={upfrontPaymentPct}
                      setUpfrontPaymentPct={setUpfrontPaymentPct}
                      paymentParts={paymentParts}
                      setPaymentParts={setPaymentParts}
                      results={results}
                      readOnly={readOnly}
                    />
                  )}

                  <OwnerOptionsSection
                    guaranteedReturnPct={guaranteedReturnPct}
                    setGuaranteedReturnPct={setGuaranteedReturnPct}
                    readOnly={readOnly}
                  />

                  <MarketAnalysisSection
                    marketValuePerSqft={marketValuePerSqft}
                    setMarketValuePerSqft={setMarketValuePerSqft}
                    readOnly={readOnly}
                    showAddButton={showCompare}
                    onAddClick={() => compareOpenRef.current?.()}
                  >
                    {showCompare && (
                      <ComparePropertyEnhanced
                        property={property}
                        propertyId={propertyId}
                        embedded
                        onOpenRef={compareOpenRef}
                        marketValuePerSqft={marketValuePerSqft}
                        setMarketValuePerSqft={setMarketValuePerSqftAuto}
                        onSelectedComparablesChange={setManualComparables}
                      />
                    )}
                  </MarketAnalysisSection>

                  {!readOnly && (
                    <div className="text-center mt-6 sm:mt-8">
                      <Button
                        onClick={handleCalculateClick}
                        size="lg"
                        className="bg-primary text-white hover:bg-primary/90 font-medium py-3 sm:py-4 md:py-6 px-4 sm:px-6 md:px-10 text-sm sm:text-base md:text-lg shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
                      >
                        <CalculatorIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-2 flex-shrink-0" />
                        Calculate Profit Potential
                      </Button>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="results" className="mt-0">
                <div
                  id="results-section"
                  className="space-y-4 sm:space-y-6 md:space-y-8"
                >
                  <ResultsPropertyCards
                    results={results}
                    homeArea={homeArea}
                    renovationTier={renovationTier}
                    extraAreaTier={extraAreaTier}
                  />

                  {results.totalProfit >= 0 ? (
                    <>
                      <BarCharts results={results} />

                      <ProfitDistributionCard
                        profitValues={profitValues}
                        setProfitValues={setProfitValues}
                        results={results}
                        profitA={profitA}
                        profitB={profitB}
                        profitC={profitC}
                      />

                      <ProfitSummaryCard results={results} />

                      <ComparisonCard results={results} />
                    </>
                  ) : (
                    <Card className="shadow-lg border-red-200 bg-gradient-to-br from-red-50 to-rose-50">
                      <CardContent className="p-5 sm:p-8">
                        <div className="flex flex-col items-center text-center gap-4">
                          <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-red-100 flex items-center justify-center">
                            <AlertTriangle className="h-7 w-7 sm:h-8 sm:w-8 text-red-500" />
                          </div>

                          <div>
                            <h3 className="text-lg sm:text-xl font-bold text-red-700 mb-1">
                              This Deal Is Not Profitable
                            </h3>
                            <p className="text-sm sm:text-base text-red-600/80">
                              Based on the current inputs, this property flip would result in a loss.
                            </p>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mt-2">
                            <div className="bg-white/80 rounded-xl p-3 sm:p-4 shadow-sm border border-red-100">
                              <div className="flex items-center justify-center gap-1.5 mb-1">
                                <TrendingDown className="h-4 w-4 text-red-500" />
                                <p className="text-xs sm:text-sm text-gray-500">Estimated Loss</p>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-red-600">
                                -${Math.abs(results.totalProfit).toLocaleString("en-US")}
                              </p>
                            </div>
                            <div className="bg-white/80 rounded-xl p-3 sm:p-4 shadow-sm border border-red-100">
                              <div className="flex items-center justify-center gap-1.5 mb-1">
                                <ArrowDownCircle className="h-4 w-4 text-red-500" />
                                <p className="text-xs sm:text-sm text-gray-500">ROI</p>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-red-600">
                                {results.roi?.toFixed(1)}%
                              </p>
                            </div>
                            <div className="bg-white/80 rounded-xl p-3 sm:p-4 shadow-sm border border-red-100">
                              <div className="flex items-center justify-center gap-1.5 mb-1">
                                <ArrowDownCircle className="h-4 w-4 text-red-500" />
                                <p className="text-xs sm:text-sm text-gray-500">Profit Margin</p>
                              </div>
                              <p className="text-lg sm:text-xl font-bold text-red-600">
                                {results.profitMargin?.toFixed(1)}%
                              </p>
                            </div>
                          </div>

                          <div className="bg-white/60 rounded-xl p-4 w-full mt-1 border border-red-100">
                            <p className="text-sm font-semibold text-gray-700 mb-2">
                              💡 Tips to improve this deal:
                            </p>
                            <ul className="text-xs sm:text-sm text-gray-600 space-y-1.5 text-left list-disc list-inside">
                              <li>Reduce renovation costs by choosing a lower tier</li>
                              <li>Check if the market value per sq ft can support a higher sale price</li>
                              <li>Consider adding extra area to increase overall value</li>
                            </ul>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  <div className="mt-6">
                    {results.totalProfit >= 0 && (
                      <div className="mb-4 space-y-3">
                        <Button
                          onClick={handleGenerateReport}
                          disabled={isGeneratingReport}
                          size="lg"
                          className="bg-primary text-white hover:bg-primary/90 font-medium w-full sm:w-auto"
                        >
                          {isGeneratingReport ? "Generating Report..." : "Generate Report PDF"}
                        </Button>
                        {reportError && (
                          <p className="text-sm text-red-600">{reportError}</p>
                        )}
                        {reportData && (
                          <div className="rounded-2xl border border-orange-100 bg-gradient-to-br from-orange-50/70 via-white to-amber-50/60 p-4 sm:p-5">
                            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                              <div className="space-y-1">
                                <p className="text-base sm:text-lg font-semibold text-slate-900">
                                  Report ready
                                </p>
                                <p className="text-xs sm:text-sm text-slate-600">
                                  Share the QR code or open the full report in a new tab.
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <a
                                  href={reportData.absoluteUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center justify-center h-10 px-4 rounded-md bg-primary text-white text-sm font-medium hover:bg-primary/90 transition-colors"
                                >
                                  Open Generated Report
                                </a>
                                <Button
                                  variant="outline"
                                  onClick={handleDownloadQr}
                                  disabled={!qrPreviewUrl}
                                  className="h-10"
                                >
                                  Download QR Code
                                </Button>
                              </div>
                            </div>
                            {qrPreviewUrl && (
                              <div className="mt-4 flex justify-center sm:justify-start">
                                <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                                  <img
                                    src={qrPreviewUrl}
                                    alt="Report QR Preview"
                                    className="w-56 h-56 sm:w-64 sm:h-64 object-contain"
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="text-center sm:text-left">
                      <Button
                        onClick={() => setActiveTabFn("input")}
                        variant="outline"
                        size="lg"
                        className="font-medium w-full sm:w-auto"
                      >
                        Modify Inputs
                      </Button>
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      <DealResultEffect
        totalProfit={results.totalProfit}
        isVisible={activeTab === "results"}
      />
    </div>
  );
}

export default HouseFlippingCalculator;
